import React, { lazy, Suspense } from 'react';
import { TFunction } from 'react-i18next';
import { Typography } from '@mui/material';

import { EventRole, IEvent } from '@interfaces/event';
import ParticipantScopes from '@constants/scopes';
import { DashboardButtonEnum } from '@constants/dashboard';
import { FAMILIARISE_STATE } from '@core/dashboard/components/HomeBottomBar/constants';
import { MULTIPLE_ROLE_STORAGE_KEY } from '@clients/ek/features/role/components/MultiRoleModal/constants';
import { UserRoles } from '@clients/ek/features/role/components/MultiRoleModal/interfaces';
import {
  IXRHrBrandTypes,
  IXRHrCredStatusTypes,
  IXRHrPersonaTypes,
  WebsocketAction,
} from '@clients/ek/interfaces';
import { IUser } from '@interfaces/user';
import { IUserAvatar } from '@interfaces/avatar';
import { GenderType } from '@features/avaturn/interfaces';
import { MINUTES_ALLOW_TO_JOIN, MINUTES_ALLOW_TO_JOIN_HOST } from '@constants';
import EventsService from '@services/EventsService';

import { env } from 'env';
import { IEventParticipant } from '@interfaces/eventParticipant';

import { APP_LOGO } from '@components/AppLogo/interfaces';
import ROUTES from '@constants/routes';
import imageOfAvatar1EK from '@public/images/ek/avatars/1-FemaleRedPolo.png';
import imageOfAvatar2EK from '@public/images/ek/avatars/2-MaleRedPolo.png';
import imageOfAvatar3EK from '@public/images/ek/avatars/3-FemaleCCUniform.png';
import imageOfAvatar4EK from '@public/images/ek/avatars/4-FemaleBA.png';
import imageOfAvatar5EK from '@public/images/ek/avatars/5-FemalePurserUniform.png';
import imageOfAvatar6EK from '@public/images/ek/avatars/6-MalePurserUniform.png';
import imageOfAvatar7EK from '@public/images/ek/avatars/7-MaleCCUniform.png';
import imageOfAvatar8EK from '@public/images/ek/avatars/8-MaleBA.png';
import LoadingProgress from '@components/LoadingProgress';
import { ReactComponent as PlaneIcon } from '@public/images/ek/plane.svg';
import { convertUnixTimeToMilliSeconds } from '@helpers/events';
import { getStorageValue, setStorageValue } from '@helpers/storage';
import { StorageTypes, StorageValues } from '@interfaces/storage';
import AppLoaderV3 from '@core/dashboard/components/AppLoader/v3/AppLoader';
import { getSDEvent } from '@clients/ek/helpers/events';

const {
  REACT_APP_MULTI_PLAYER_TAG,
  REACT_APP_MAYA_TAGS,
  REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG,
  REACT_APP_SEP_DISABLE_EVENT_BEFORE_START_TIME_MINS,
  REACT_APP_SEP_TRAINING_EVENT_TAG,
  REACT_APP_SD_EVENT_TAG,
  REACT_APP_SEP_PRACTICE_EVENT_TAG,
  REACT_APP_SCHEDULED_EVENT_TAGS,
} = env;

const MultiRoleModal = lazy(
  () => import('@clients/ek/features/role/components/MultiRoleModal'),
);

const FeedbackModal = lazy(
  () => import('@clients/ek/features/feedback/components/FeedbackModal'),
);

const SkyLink = lazy(() => import('@clients/ek/components/SkyLink'));

const reactMultiPlayerTag = REACT_APP_MULTI_PLAYER_TAG || 'MultiPlayer';

const REACT_APP_SEP_EVENT_VISIBILITY_START_TIME_MINS = Number(
  REACT_APP_SEP_DISABLE_EVENT_BEFORE_START_TIME_MINS || 15,
);

export const clientSpecificMultiRoleModal = () => (
  <Suspense>
    <MultiRoleModal />
  </Suspense>
);

export const clientSpecificFeedbackModal = () => {
  return (
    <Suspense fallback={<LoadingProgress />}>
      <FeedbackModal />
    </Suspense>
  );
};

export const clientSpecificDashboardExtension = () => {
  return (
    <Suspense>
      <SkyLink />
    </Suspense>
  );
};

export const clientSpecificLastLoadingStepIcon = () => <PlaneIcon />;

export const clientSpecificUserEventPermissions = ({
  role,
  roleScopes,
}: {
  role?: EventRole;
  roleScopes?: { roleName: string; permissions: ParticipantScopes[] }[];
}) => {
  let permissionsRole = UserRoles.EVENT_PARTICIPANT;
  switch (role) {
    case EventRole.Admin: {
      permissionsRole = UserRoles.FACILITATOR;
      break;
    }
    case EventRole.Coordinator: {
      permissionsRole = UserRoles.COORDINATOR;
      break;
    }
  }

  const permissions = roleScopes?.find(
    ({ roleName }) => roleName === permissionsRole,
  )?.permissions;

  return permissions || [];
};

export const clientSpecificDashboardButtonDisabled = ({
  buttonName,
  familiarizeState,
}: {
  buttonName: DashboardButtonEnum;
  familiarizeState: string;
}) => {
  const activeButton = familiarizeState;

  switch (buttonName) {
    case DashboardButtonEnum.CHAT_BUTTON: {
      return activeButton !== FAMILIARISE_STATE.SESSION_CHAT;
    }

    case DashboardButtonEnum.PARTICIPANTS_BUTTON: {
      return activeButton !== FAMILIARISE_STATE.VIDEO_CONTROLS;
    }

    case DashboardButtonEnum.RISE_HAND_BUTTON: {
      return activeButton !== FAMILIARISE_STATE.RISE_HAND;
    }

    case DashboardButtonEnum.SCREEN_SHARE_BUTTON: {
      return activeButton !== FAMILIARISE_STATE.SCREEN_SHARE;
    }

    default:
      return true;
  }
};

export const clientSpecificUserEventRole = () => {
  const multipleRoleStorage = localStorage.getItem(MULTIPLE_ROLE_STORAGE_KEY);

  if (!multipleRoleStorage) {
    return null;
  }

  if (multipleRoleStorage == UserRoles.FACILITATOR) {
    return EventRole.Admin;
  } else if (multipleRoleStorage == UserRoles.EVENT_PARTICIPANT) {
    return '';
  }
};

export const clientSpecificUserAvatars = ({
  role,
  brand,
  gender,
  avatars,
}: {
  role: string;
  brand: string;
  gender: string;
  avatars: IUserAvatar[];
}) => {
  if (brand !== IXRHrBrandTypes.SD) {
    return avatars;
  }

  const isAdmin =
    role &&
    role !== '' &&
    [EventRole.Admin, EventRole.Coordinator].includes(role as EventRole);

  const id = `${gender === GenderType.MALE ? 'M' : 'F'}|${
    IXRHrCredStatusTypes.FT
  }|${IXRHrBrandTypes.SD_FACILITATOR}`;

  return avatars.filter(({ assetId }) => {
    return (isAdmin && assetId === id) || (!isAdmin && assetId !== id);
  });
};

export const clientSpecificProfileUserName = (user: IUser) => {
  if (user.hrData) {
    const { name, nickName } = user.hrData;
    return (
      <>
        <Typography>{name}</Typography>
        {nickName ? <Typography>({nickName})</Typography> : null}
      </>
    );
  }

  return <Typography>{user.name}</Typography>;
};

export const clientSpecificProfileUserEmail = (user: IUser) => {
  return user.hrData ? user.hrData?.personalEmail : user.email;
};

export const clientSpecificProfileUserGender = (user: IUser) => {
  return user.hrData
    ? user.hrData?.gender.toLowerCase() === 'm'
      ? GenderType.MALE
      : GenderType.FEMALE
    : '';
};

export const clientSpecificVirtualAssistant = ({
  tags,
  translate,
}: {
  tags?: string[];
  translate: TFunction<'translation'>;
}) => {
  if (tags && Array.isArray(tags)) {
    const MAYA_TAGS =
      REACT_APP_MAYA_TAGS?.split(',').map((x) => x.toLowerCase().trim()) || [];

    const isMaya = tags?.some((tag) => MAYA_TAGS.includes(tag.toLowerCase()));

    if (isMaya) {
      return {
        icon: <img src={require('@public/images/assistant/maya.png')} />,
        label: translate('tooltips.maya'),
      };
    }
  }

  return {
    icon: <img src={require('@public/images/assistant/mira.png')} />,
    label: translate('tooltips.mira'),
  };
};

export const clientSpecificHasMultiplayerTag = (tags?: string[]) => {
  return tags && Array.isArray(tags) && tags.includes(reactMultiPlayerTag);
};

export const clientSpecificJoinEventSchedulerMinToJoin = (user?: IUser) => {
  return user?.hrData?.persona?.toUpperCase() ===
    IXRHrPersonaTypes.HR_Facilitator
    ? MINUTES_ALLOW_TO_JOIN_HOST
    : MINUTES_ALLOW_TO_JOIN;
};

export const clientSpecificCanCreateNewAvatar = ({
  brand,
  listOfAvatarsAlreadyCreated,
}: {
  brand: string;
  listOfAvatarsAlreadyCreated: IUserAvatar[];
}) => {
  // For non SD (HR), we can only create 1 avatar
  if (brand !== IXRHrBrandTypes.SD) {
    const canCreateHRAvatar =
      listOfAvatarsAlreadyCreated.length === 0 ? true : false;
    return canCreateHRAvatar;
  }

  // for SD, we can create up to 5 avatars: 4 non uniformed and 1 uniformed
  const maxNumAvatar = parseInt(env.REACT_APP_MAX_NUMBER_OF_AVATARS || '5');

  if (listOfAvatarsAlreadyCreated.length < maxNumAvatar - 1) {
    return true;
  } else {
    // if there are already 4 in the list BUT one of them is the uniform, then we can add
    const hasUniform = listOfAvatarsAlreadyCreated.some((obj) =>
      obj.tags.includes('UNIFORM'),
    );

    if (hasUniform && listOfAvatarsAlreadyCreated.length < maxNumAvatar) {
      return true;
    }
    return false;
  }
};

export const clientSpecificHideGenericAvaturnType = (brand: string) => {
  if (brand) {
    switch (brand.toUpperCase()) {
      case IXRHrBrandTypes.SD:
        return true;
      default:
        return false;
    }
  } else {
    return false;
  }
};

export const clientSpecificIsSD = (brand: string) => {
  return brand === IXRHrBrandTypes.SD;
};
export const clientSpecificHideRevokeAvatarConsentLink = (brand: string) => {
  return clientSpecificIsSD(brand);
};

export const clientSpecificHideAvaturnCreation = ({
  user,
  brand,
}: {
  user: IUser;
  brand: string;
}) => {
  if (
    clientSpecificIsSD(brand) &&
    user?.hrData?.persona === IXRHrPersonaTypes.Abinitio
  ) {
    const hasUniformed = user?.avatars.find(({ tags = [] }) =>
      tags.includes('UNIFORM'),
    );

    return !!hasUniformed;
  }

  return false;
};

export const clientSpecificSDScreenShareButtonDisabled = ({
  eventRole,
  brand,
}: {
  eventRole?: EventRole;
  brand: string;
}) => {
  return clientSpecificIsSD(brand) && eventRole === EventRole.None;
};

export const clientSpecificAvatarDeletion = async ({
  events,
  user,
  avatar,
  currentEventId,
  currentEventParticipant,
  sendMessageToUnreal,
  setCurrentEventParticipant,
}: {
  events: IEvent[];
  user: IUser;
  avatar: IUserAvatar;
  currentEventId?: string;
  currentEventParticipant: IEventParticipant | null;
  sendMessageToUnreal: (message: any) => void;
  setCurrentEventParticipant: React.Dispatch<
    React.SetStateAction<IEventParticipant | null>
  >;
}) => {
  const sdHubEvent = REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG
    ? events.find((event) =>
        event.eventTags?.includes(REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG),
      )
    : null;

  if (
    user.hrData.brand.toUpperCase() == IXRHrBrandTypes.SD &&
    avatar.tags?.includes('UNIFORM') &&
    currentEventId &&
    sdHubEvent
  ) {
    const isCurrentSdEvent = sdHubEvent.eventId === currentEventId;

    let state: { [key: string]: any } | null = null;
    if (isCurrentSdEvent) {
      state = currentEventParticipant?.state
        ? JSON.parse(currentEventParticipant.state)
        : null;
    } else {
      const eventParticipant = await EventsService.getEvent(sdHubEvent.eventId);

      state = eventParticipant?.state
        ? JSON.parse(eventParticipant.state)
        : null;
    }

    if (state && state.AssistantHistory) {
      state.AssistantHistory.replace(
        /\(TagName="iXR\.Maya\.SS(?!01|02|03|04)[^"]*"\),?/g,
        '',
      )
        .replace(/,\)/g, ')')
        .replace(/,\)/g, ')')
        .replace(/\(\s*\)/g, '');

      state.bIsUniformTryOnCompleted = false;
      state.bIsMagicMirrorCompleted = false;
      state.bIsCrowningMomentCompleted = false;

      await EventsService.setEventUserData(sdHubEvent.eventId, {
        state: JSON.stringify(state),
      });

      if (isCurrentSdEvent) {
        sendMessageToUnreal({
          action: WebsocketAction.EventProfileStateUpdated,
        });

        setCurrentEventParticipant((prevState) => {
          if (!prevState) {
            return prevState;
          }

          return {
            ...prevState,
            state: JSON.stringify(state),
          };
        });
      }
    }

    return;
  }
};

export const clientSpecificLogo = (user?: IUser) => {
  // this code is called also when logged out so we cannot use useAvaturnHelper()
  if (user) {
    if (user.hrData.brand.toUpperCase() !== IXRHrBrandTypes.SD) {
      return {
        logo: APP_LOGO.DEFAULT,
        shouldStickToTheTop: false,
      };
    } else {
      return {
        logo: APP_LOGO.ALT,
        shouldStickToTheTop: true,
      };
    }
  } else {
    const currentURL = window.location.href;
    if (currentURL.includes(ROUTES.CUSTOM_AVATURN)) {
      return {
        logo: APP_LOGO.ALT,
        shouldStickToTheTop: true,
      };
    } else {
      return {
        logo: APP_LOGO.DEFAULT,
        shouldStickToTheTop: false,
      };
    }
  }
};

export const clientSpecificCustomTextDeleteEditActionsOnAvatar = ({
  brand,
  avatar,
}: {
  brand: string;
  avatar: IUserAvatar | null;
}) => {
  let canEdit = true,
    canDelete = true,
    cannotEditAvatarNotification,
    customDeleteAvatarTitle = '',
    customDeleteAvatarText = '';

  if (brand === IXRHrBrandTypes.SD && avatar?.tags?.includes('UNIFORM')) {
    customDeleteAvatarTitle = 'deleteUniformedAvatarModal.title';
    customDeleteAvatarText = 'deleteUniformedAvatarModal.text';
    canEdit = false;
    cannotEditAvatarNotification = 'cannotEditAvatar';
    canDelete = true;
  }

  return {
    canEdit,
    canDelete,
    cannotEditAvatarNotification,
    customDeleteAvatarTitle,
    customDeleteAvatarText,
  };
};

export const clientSpecificAvaturnCarousel = () => {
  return [
    imageOfAvatar1EK,
    imageOfAvatar2EK,
    imageOfAvatar3EK,
    imageOfAvatar4EK,
    imageOfAvatar5EK,
    imageOfAvatar6EK,
    imageOfAvatar7EK,
    imageOfAvatar8EK,
  ];
};

export const clientSpecificSEPVisibilityCarouselEvents = ({
  events,
  user,
}: {
  events: (IEvent & { inviteStartTime: number })[];
  user: IUser;
}) => {
  if (events.length && user.hrData.brand.toUpperCase() === IXRHrBrandTypes.SD) {
    const now = Date.now();
    const minutesInMs =
      REACT_APP_SEP_EVENT_VISIBILITY_START_TIME_MINS * 60 * 1000;

    // Find training events that should be disabled
    const shouldDisableTraining = events.filter(
      (event) =>
        event.eventTags?.includes(REACT_APP_SEP_TRAINING_EVENT_TAG as string) &&
        now < event.inviteStartTime &&
        Math.abs(now - event.inviteStartTime) > minutesInMs,
    );

    if (shouldDisableTraining.length > 0) {
      // Disable training events
      shouldDisableTraining.forEach((event) => {
        event.isDisabled = true;
      });

      // Enable practice events (only if we disabled training events)
      events
        .filter((event) =>
          event.eventTags?.includes(REACT_APP_SEP_PRACTICE_EVENT_TAG as string),
        )
        .forEach((event) => {
          event.isDisabled = false;
        });
    }

    // disable scheduled events 15 mins before invite start time
    events
      .filter(
        (event) =>
          event.isScheduled &&
          now < convertUnixTimeToMilliSeconds(event.inviteStartTime) &&
          Math.abs(now - convertUnixTimeToMilliSeconds(event.inviteStartTime)) >
            minutesInMs,
      )
      .forEach((event) => {
        event.isDisabled = true;
      });

    // disable scheduled events 1h after end time
    events
      .filter(
        (event) => event.isScheduled && now > (event.endTime + 3600) * 1000,
      )
      .forEach((event) => {
        event.isDisabled = true;
      });
  }

  return events;
};

export const clientSpecificFirstTimeSDCarouselEvents = ({
  events,
  user,
}: {
  events: (IEvent & { state: string | null | undefined })[];
  user: IUser;
}): (IEvent & { state: string | null | undefined })[] => {
  if (
    !events.length ||
    !REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG ||
    !REACT_APP_SD_EVENT_TAG ||
    user.hrData.brand.toUpperCase() !== IXRHrBrandTypes.SD
  ) {
    return events;
  }

  const sdHubEvent = events.find((event) =>
    event.eventTags?.includes(REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG),
  );

  if (!sdHubEvent) {
    return events;
  }

  if (sdHubEvent.state) {
    const state = JSON.parse(sdHubEvent.state);

    const isTutorialComplete = state?.bIsTutorialCompleted === true;

    if (isTutorialComplete) {
      return events;
    }
  }

  // Create protected tags set once, outside the function if possible
  const createProtectedTagsSet = () => {
    const scheduledTags =
      REACT_APP_SCHEDULED_EVENT_TAGS?.split(',').map((tag) => tag.trim()) || [];

    return new Set(
      [
        ...scheduledTags,
        REACT_APP_FULL_TIME_SD_HUB_EVENT_TAG,
        REACT_APP_SEP_PRACTICE_EVENT_TAG,
      ].filter(Boolean),
    ); // Remove any undefined/null values
  };

  const protectedTags = createProtectedTagsSet();

  // Main processing function
  return events.map((event) => {
    const hasProtectedTag = event.eventTags?.some((tag) =>
      protectedTags.has(tag),
    );

    if (!hasProtectedTag) {
      event.isDisabled = true;
      event.isFirstTimeTooltip = true;
    }

    return event;
  });
};

export const clientSpecificSDTooltipTypeCarouselEvents = ({
  user,
  events,
}: {
  events: IEvent[];
  user: IUser;
}) => {
  if (
    events.length &&
    user.hrData.brand.toUpperCase() === IXRHrBrandTypes.SD &&
    user.hrData.gradeCode
  ) {
    events.forEach((event) => {
      const isGradeCodeDisabled = event?.accessControlList?.find(
        (x) => x.path === 'gradeCode' && x.value === user.hrData.gradeCode,
      )?.disabled;

      if (isGradeCodeDisabled) {
        event.isNotAvailableForGradeCodeTooltip = true;
      }
    });
  }

  return events;
};

export const clientSpecificAppLoader = () => {
  const transitionLoadingScreen: any = getStorageValue(
    StorageValues.TransitionLoadingScreen,
    StorageTypes.Session,
  );

  if (transitionLoadingScreen) {
    return AppLoaderV3;
  }

  return null;
};

export const clientSpecificEventFullLoadingPage = () => {
  const transitionLoadingScreen: any = getStorageValue(
    StorageValues.TransitionLoadingScreen,
    StorageTypes.Session,
  );

  if (transitionLoadingScreen) {
    return <LoadingProgress fullHeight />;
  }

  return null;
};

export const clientSpecificMandatoryLogoutModal = (events: IEvent[]) => {
  const targetEvent = getSDEvent(events);

  return {
    onCancel: () => {
      if (targetEvent) {
        setStorageValue(
          StorageValues.TransitionLoadingScreen,
          { isActive: true },
          StorageTypes.Session,
        );
        window.location.assign(ROUTES.EVENT_PAGE(targetEvent.eventId));
      } else {
        console.log(
          'No matching event found with SD, FullTimeSDHub tags (excluding LINUX)',
        );
      }
    },
  };
};
